import React from "react";
import { Link } from "react-router-dom";
import { PhoneIcon as Phone, EnvelopeSimpleIcon as EnvelopeSimple, MapPinIcon as MapPin } from "@phosphor-icons/react";

/**
 * Single source of truth for "how to reach Armstrong K9 Registry LLC".
 *
 * Why this exists:
 *   D&B (Dun & Bradstreet) denied our D-U-N-S request 2026-06-16 with
 *   reason "Unable to reach the business to confirm existence". Their
 *   verification routine inspects the public site for:
 *     • a callable phone number
 *     • a verifiable mailing address (matched against state LLC filings)
 *     • the registered legal-entity name (with LLC suffix)
 *     • a reachable email
 *
 *   We had email-only — and even that email link was sitting behind the
 *   fixed "Made with Emergent" badge on mobile.
 *
 * This component renders all four channels (email, phone, address,
 * contact form) so D&B (or any verifier) can cross-check within a few
 * seconds — and drops in to LandingPage, PublicFooter, About, and the
 * Contact page so the answer is identical everywhere.
 *
 * Env values (frontend/.env):
 *   REACT_APP_BUSINESS_PHONE      — e.g. "(530) 364-4699"
 *   REACT_APP_BUSINESS_ADDRESS    — e.g. "4054 Highway 273, Anderson, CA 96007"
 *   REACT_APP_LEGAL_ENTITY        — e.g. "Armstrong K9 Registry LLC"
 *   REACT_APP_SUPPORT_EMAIL       — e.g. "registry@armstrongk9registry.com"
 *
 * Missing values gracefully omit the row in production. In dev/preview
 * a red "NOT SET" placeholder is shown so we don't ship a denial-causing
 * gap to prod by accident.
 */
const BUSINESS_EMAIL =
  process.env.REACT_APP_SUPPORT_EMAIL || "hello@armstrongk9registry.com";
const BUSINESS_PHONE_RAW = process.env.REACT_APP_BUSINESS_PHONE || "";
const BUSINESS_PHONE_HREF = BUSINESS_PHONE_RAW.replace(/[^\d+]/g, "");
const BUSINESS_ADDRESS = process.env.REACT_APP_BUSINESS_ADDRESS || "";
const BUSINESS_HOURS = process.env.REACT_APP_BUSINESS_HOURS || "";
export const LEGAL_ENTITY =
  process.env.REACT_APP_LEGAL_ENTITY || "Armstrong K9 Registry";
/** Friendly DBA / trade name — used in marketing copy and footer
 * copyright. The LLC suffix lives in legal docs + Schema.org legalName. */
export const DBA_NAME =
  process.env.REACT_APP_DBA_NAME || "Armstrong K9 Registry";
const IS_DEV =
  process.env.NODE_ENV !== "production" ||
  /preview|localhost/i.test(process.env.REACT_APP_SENTRY_ENV || "");

export function hasBusinessPhone() {
  return Boolean(BUSINESS_PHONE_HREF);
}
export function hasBusinessAddress() {
  return Boolean(BUSINESS_ADDRESS);
}

/** Compact one-line variant for footer rows. */
export function BusinessContactInline({ tone = "dark" }) {
  const isDark = tone === "dark";
  const link = isDark
    ? "text-gold hover:text-white"
    : "text-[#8A6A00] hover:text-ink";
  const text = isDark ? "text-[#C4C4C4]/85" : "text-[#3B3B3B]";
  return (
    <div
      data-testid="business-contact-inline"
      className={`flex flex-wrap items-center gap-x-5 gap-y-2 text-xs ${text}`}
    >
      <a
        href={`mailto:${BUSINESS_EMAIL}`}
        className={`inline-flex items-center gap-1.5 font-mono ${link}`}
        data-testid="business-contact-email"
      >
        <EnvelopeSimple size={14} weight="duotone" />
        {BUSINESS_EMAIL}
      </a>
      {BUSINESS_PHONE_RAW && (
        <a
          href={`tel:${BUSINESS_PHONE_HREF}`}
          className={`inline-flex items-center gap-1.5 font-mono ${link}`}
          data-testid="business-contact-phone"
        >
          <Phone size={14} weight="duotone" />
          {BUSINESS_PHONE_RAW}
        </a>
      )}
      {BUSINESS_ADDRESS && (
        <span
          className="inline-flex items-center gap-1.5"
          data-testid="business-contact-address"
        >
          <MapPin size={14} weight="duotone" />
          {BUSINESS_ADDRESS}
        </span>
      )}
      <Link
        to="/contact?topic=general&subject=General%20inquiry"
        className={`inline-flex items-center gap-1.5 font-semibold ${link}`}
        data-testid="business-contact-form-link"
      >
        Contact us →
      </Link>
    </div>
  );
}

/** Hero card for landing-page / about-page footers. */
export function BusinessContactCard({ className = "" }) {
  return (
    <section
      data-testid="business-contact-card"
      aria-label="Reach Armstrong K9 Registry"
      className={`bg-white border border-[#E8DCAA] rounded-2xl p-5 sm:p-6 shadow-[0_8px_30px_rgba(193,162,39,0.08)] ${className}`}
    >
      <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#8A6A00]">
        Reach Armstrong K9 Registry
      </p>
      <h3 className="font-spectral text-2xl text-ink mt-1">
        Talk to a real human.
      </h3>
      <p className="text-[13px] text-[#5C5C5C] mt-2 leading-relaxed max-w-xl">
        Verification agencies, members, and the press: any of these three
        channels reaches the registry team. Email is monitored Monday–Friday;
        the contact form lands directly in admin inbox.
      </p>
      {BUSINESS_HOURS && (
        <p
          data-testid="business-card-hours"
          className="text-[12px] text-[#5C5C5C] mt-2 inline-flex items-center gap-1.5"
        >
          <span
            className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500"
            aria-hidden="true"
          />
          <span className="font-semibold text-ink">Business hours:</span>{" "}
          {BUSINESS_HOURS}
        </p>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
        <a
          href={`mailto:${BUSINESS_EMAIL}`}
          data-testid="business-card-email"
          className="rounded-xl border border-[#EAEAEA] hover:border-gold/60 hover:bg-[#FBF7EC] p-4 transition-all block"
        >
          <EnvelopeSimple size={20} weight="duotone" className="text-gold mb-2" />
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#6B6B6B]">
            Email
          </p>
          <p className="text-sm font-mono text-ink break-all mt-1">
            {BUSINESS_EMAIL}
          </p>
        </a>

        {BUSINESS_PHONE_RAW ? (
          <a
            href={`tel:${BUSINESS_PHONE_HREF}`}
            data-testid="business-card-phone"
            className="rounded-xl border border-[#EAEAEA] hover:border-gold/60 hover:bg-[#FBF7EC] p-4 transition-all block"
          >
            <Phone size={20} weight="duotone" className="text-gold mb-2" />
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#6B6B6B]">
              Phone
            </p>
            <p className="text-sm font-mono text-ink mt-1">{BUSINESS_PHONE_RAW}</p>
          </a>
        ) : IS_DEV ? (
          <div
            data-testid="business-card-phone-placeholder"
            className="rounded-xl border border-dashed border-crimson/40 bg-[#FEE2E2]/40 p-4"
            title="Set REACT_APP_BUSINESS_PHONE in frontend/.env so D&B + members can call."
          >
            <Phone size={20} weight="duotone" className="text-crimson mb-2" />
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-crimson">
              Phone · NOT SET
            </p>
            <p className="text-[11px] text-[#5C5C5C] mt-1 leading-snug">
              Set <span className="font-mono">REACT_APP_BUSINESS_PHONE</span> in
              <span className="font-mono"> frontend/.env</span> to make this
              live (dev-only banner).
            </p>
          </div>
        ) : null}

        {BUSINESS_ADDRESS ? (
          <a
            href={`https://maps.google.com/?q=${encodeURIComponent(BUSINESS_ADDRESS)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="business-card-address"
            className="rounded-xl border border-[#EAEAEA] hover:border-gold/60 hover:bg-[#FBF7EC] p-4 transition-all block"
          >
            <MapPin size={20} weight="duotone" className="text-gold mb-2" />
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#6B6B6B]">
              Address
            </p>
            <p className="text-[13px] text-ink mt-1 leading-snug">
              {BUSINESS_ADDRESS}
            </p>
          </a>
        ) : IS_DEV ? (
          <div
            data-testid="business-card-address-placeholder"
            className="rounded-xl border border-dashed border-crimson/40 bg-[#FEE2E2]/40 p-4"
          >
            <MapPin size={20} weight="duotone" className="text-crimson mb-2" />
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-crimson">
              Address · NOT SET
            </p>
            <p className="text-[11px] text-[#5C5C5C] mt-1 leading-snug">
              Set <span className="font-mono">REACT_APP_BUSINESS_ADDRESS</span> in
              <span className="font-mono"> frontend/.env</span>.
            </p>
          </div>
        ) : null}

        <Link
          to="/contact?topic=general&subject=General%20inquiry"
          data-testid="business-card-form-link"
          className="rounded-xl border border-[#EAEAEA] hover:border-gold/60 hover:bg-[#FBF7EC] p-4 transition-all block"
        >
          <svg
            className="text-gold mb-2"
            width="20" height="20" viewBox="0 0 24 24"
            fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          >
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
          </svg>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#6B6B6B]">
            Contact form
          </p>
          <p className="text-sm font-semibold text-ink mt-1">Open form →</p>
        </Link>
      </div>
    </section>
  );
}
